import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Coins, Repeat, Star, Users, Check, X, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function fmt(template, data = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, k) => {
    const v = data[k];
    return v == null ? "" : typeof v === "number" ? v.toLocaleString("it-IT") : v;
  });
}

const ICONS = {
  transfer: { icon: Coins, color: "var(--accent)", bg: "var(--accent-soft)" },
  trade_new: { icon: Repeat, color: "var(--primary)", bg: "var(--primary-soft)" },
  trade_accepted: { icon: Check, color: "#059669", bg: "#d1fae5" },
  trade_rejected: { icon: X, color: "#d9532f", bg: "#fdeae5" },
  rating: { icon: Star, color: "var(--accent)", bg: "var(--accent-soft)" },
  referral: { icon: Users, color: "#059669", bg: "#d1fae5" },
  message: { icon: MessageCircle, color: "var(--primary)", bg: "var(--primary-soft)" },
  trade_completed: { icon: Check, color: "#059669", bg: "#d1fae5" },
  trade_started: { icon: Repeat, color: "var(--primary)", bg: "var(--primary-soft)" },
  trade_refunded: { icon: Coins, color: "var(--accent)", bg: "var(--accent-soft)" },
  trade_cancel_requested: { icon: Repeat, color: "var(--accent)", bg: "var(--accent-soft)" },
  trade_cancelled: { icon: X, color: "#d9532f", bg: "#fdeae5" },
  trade_disputed: { icon: X, color: "#d9532f", bg: "#fdeae5" },
  trade_admin_refund: { icon: Coins, color: "var(--accent)", bg: "var(--accent-soft)" },
  trade_admin_payout: { icon: Check, color: "#059669", bg: "#d1fae5" },
  trade_completion_declared: { icon: Check, color: "var(--accent)", bg: "var(--accent-soft)" },
  trade_completion_disputed: { icon: X, color: "#d9532f", bg: "#fdeae5" },
  trade_admin_split: { icon: Coins, color: "var(--accent)", bg: "var(--accent-soft)" },
};

export default function NotificationBell() {
  const { refresh } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: 0, items: [] });
  const lastUnread = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
      if (data.unread > lastUnread.current) refresh();
      lastUnread.current = data.unread;
    } catch { /* ignore */ }
  }, [refresh]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const togglePanel = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setData((d) => ({ unread: 0, items: d.items.map((i) => ({ ...i, read: true })) }));
    lastUnread.current = 0;
  };

  const go = async (n) => {
    setOpen(false);
    if (data.unread) await markAll();
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative">
      <button onClick={togglePanel} data-testid="notifications-btn" title={t("notif_title")}
        aria-expanded={open} aria-controls="notifications-panel"
        className="relative z-[60] w-9 h-9 rounded-full flex items-center justify-center text-[#5f6b62] hover:bg-[#f4f0e8] hover:text-[#d97706] transition-all">
        <Bell className="w-5 h-5" />
        {data.unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d9532f] text-white text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="notifications-backdrop" />
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              id="notifications-panel"
              className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[340px] sm:max-w-[92vw] bg-white rounded-2xl border border-[#e5dfd3] card-shadow z-50 overflow-hidden" data-testid="notifications-panel">
              <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-1.5 px-4 py-3 border-b border-[#f0ebe0] text-center sm:text-left">
                <p className="font-head font-bold" data-testid="notifications-title">{t("notif_title")}</p>
                {data.unread > 0 && <button onClick={markAll} data-testid="notifications-mark-read" className="text-xs font-semibold text-[#d97706] hover:underline">{t("notif_mark_read")}</button>}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {data.items.length === 0 ? (
                  <p className="text-sm text-[#5f6b62] text-center py-10" data-testid="notifications-empty">{t("notif_empty")}</p>
                ) : data.items.map((n) => {
                  const meta = ICONS[n.type] || ICONS.transfer;
                  return (
                    <button key={n.id} onClick={() => go(n)} data-testid={`notification-item-${n.id}`}
                      className={`relative w-full text-center sm:text-left flex flex-col sm:flex-row items-center sm:items-start gap-2.5 sm:gap-3 px-4 py-4 sm:py-3 border-b border-[#f0ebe0] last:border-0 hover:bg-[#f4f0e8] ${n.read ? "" : "bg-[#fffbeb]"}`}>
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: meta.bg }}><meta.icon className="w-4 h-4" style={{ color: meta.color }} /></span>
                      <span className="min-w-0 w-full flex-1">
                        <span data-testid={`notification-text-${n.id}`} className={`block text-sm leading-snug whitespace-normal break-words ${n.read ? "text-[#5f6b62]" : "text-[#1b241e] font-medium"}`}>{fmt(t(`notif_${n.type}`), n.type === "listing_reported" ? { ...n.data, reason: t(`report_reason_${n.data?.reason || "other"}`) } : n.data)}</span>
                        {n.data?.note && <span data-testid={`notification-note-${n.id}`} className="block text-xs text-[#5f6b62] whitespace-normal break-words mt-1">“{n.data.note}”</span>}
                        <span data-testid={`notification-date-${n.id}`} className="block text-[11px] text-[#7d8c81] mt-1">{new Date(n.created_at).toLocaleString()}</span>
                      </span>
                      {!n.read && <span data-testid={`notification-unread-${n.id}`} className="absolute right-3 top-3 sm:static w-2 h-2 rounded-full bg-[#d97706] sm:mt-2 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
